import { useState, useEffect } from 'react';

/**
 * Хук для работы с localStorage
 * @param key Ключ для сохранения в localStorage
 * @param initialValue Начальное значение
 * @returns [value, setValue] - текущее значение и функция для его изменения
 */
export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((val: T) => T)) => void] {
  // Получаем начальное значение из localStorage или используем переданное значение
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = localStorage.getItem(key);
      // Если item существует, парсим и возвращаем, иначе возвращаем initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Ошибка при получении ${key} из localStorage:`, error);
      return initialValue;
    }
  });

  // Функция для обновления значения в localStorage и state
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Разрешаем value быть функцией, как в useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      
      // Сохраняем в state
      setStoredValue(valueToStore);
      
      // Сохраняем в localStorage
      localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Ошибка при сохранении ${key} в localStorage:`, error);
    }
  };

  // Синхронизируем значение при изменении ключа
  useEffect(() => {
    try {
      const item = localStorage.getItem(key);
      if (item) {
        setStoredValue(JSON.parse(item));
      } else {
        localStorage.setItem(key, JSON.stringify(initialValue));
      }
    } catch (error) {
      console.error(`Ошибка при синхронизации ${key} с localStorage:`, error);
    }
  }, [key, initialValue]);

  return [storedValue, setValue];
} 